import { action, observable, makeObservable } from 'mobx';
import moment from 'moment';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from '../Enums';

export interface INotesEntityAttributes extends IModelAttributes {
	noteType: Enums.noteType;
	content: string;
	location: Enums.locationType;
	creationDatetime: Date;
	showToCustomers: boolean;
	auditNote: boolean;

	bookings: Array<Models.NoteBooking | Models.INoteBookingAttributes>;
	userNoteIsAboutId?: string;
	userNoteIsAbout?: Models.UserEntity | Models.IUserEntityAttributes;
	ferryTripId?: string;
	ferryTrip?: Models.FerryTripEntity | Models.IFerryTripEntityAttributes;
	users: Array<Models.UserNote | Models.IUserNoteAttributes>;
}
@entity('NotesEntity', 'Notes')
export default class NotesEntity extends Model implements INotesEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<NotesEntity, Enums.noteType>()
	public noteType: Enums.noteType;

	/**
	 * The text of the note
	 */
	@observable
	@attribute<NotesEntity, string>()
	public content: string;

	/**
	 * The location that the note was created in the application. More specifically whether it was made through the booking wizard or the check-in flow
	 */
	@observable
	@attribute<NotesEntity, Enums.locationType>()
	public location: Enums.locationType;

	/**
	 * The datetime that the note was created. We need this to be stored on the entity because notes will be created in a background job and could result in created datetimes that are wrong.
	 */
	@observable
	@attribute<NotesEntity, Date>()
	public creationDatetime: Date;

	@observable
	@attribute<NotesEntity, boolean>()
	public showToCustomers: boolean;

	@observable
	@attribute<NotesEntity, boolean>()
	public auditNote: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public bookings: Models.NoteBooking[] = [];

	@observable
	@attribute()
	public userNoteIsAboutId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public userNoteIsAbout: Models.UserEntity;

	@observable
	@attribute()
	public ferryTripId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public ferryTrip: Models.FerryTripEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public users: Models.UserNote[] = [];

	constructor(attributes?: Partial<INotesEntityAttributes>) {
		super(attributes);

		this.bookings = [];
		this.users = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<INotesEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.noteType !== undefined) {
				this.noteType = attributes.noteType;
			}
			if (attributes.content !== undefined) {
				this.content = attributes.content;
			}
			if (attributes.location !== undefined) {
				this.location = attributes.location;
			}
			if (attributes.creationDatetime !== undefined) {
				if (attributes.creationDatetime === null) {
					this.creationDatetime = attributes.creationDatetime;
				} else {
					this.creationDatetime = moment(attributes.creationDatetime).toDate();
				}
			}
			if (attributes.showToCustomers !== undefined) {
				this.showToCustomers = attributes.showToCustomers;
			}
			if (attributes.auditNote !== undefined) {
				this.auditNote = attributes.auditNote;
			}
			if (attributes.bookings !== undefined && Array.isArray(attributes.bookings)) {
				if (!this.bookings) {
					this.bookings = [];
				}
				for (const model of attributes.bookings) {
					if (model instanceof Models.NoteBooking) {
						this.bookings.push(model);
					} else {
						this.bookings.push(new Models.NoteBooking(model));
					}
				}
			}
			if (attributes.userNoteIsAboutId !== undefined) {
				this.userNoteIsAboutId = attributes.userNoteIsAboutId;
			}
			if (attributes.userNoteIsAbout !== undefined) {
				if (attributes.userNoteIsAbout === null) {
					this.userNoteIsAbout = attributes.userNoteIsAbout;
				} else if (attributes.userNoteIsAbout instanceof Models.UserEntity) {
					this.userNoteIsAbout = attributes.userNoteIsAbout;
					this.userNoteIsAboutId = attributes.userNoteIsAbout.id;
				} else {
					this.userNoteIsAbout = new Models.UserEntity(attributes.userNoteIsAbout);
					this.userNoteIsAboutId = this.userNoteIsAbout.id;
				}
			}
			if (attributes.ferryTripId !== undefined) {
				this.ferryTripId = attributes.ferryTripId;
			}
			if (attributes.ferryTrip !== undefined) {
				if (attributes.ferryTrip === null) {
					this.ferryTrip = attributes.ferryTrip;
				} else if (attributes.ferryTrip instanceof Models.FerryTripEntity) {
					this.ferryTrip = attributes.ferryTrip;
					this.ferryTripId = attributes.ferryTrip.id;
				} else {
					this.ferryTrip = new Models.FerryTripEntity(attributes.ferryTrip);
					this.ferryTripId = this.ferryTrip.id;
				}
			}
			if (attributes.users !== undefined && Array.isArray(attributes.users)) {
				if (!this.users) {
					this.users = [];
				}
				for (const model of attributes.users) {
					if (model instanceof Models.UserNote) {
						this.users.push(model);
					} else {
						this.users.push(new Models.UserNote(model));
					}
				}
			}
		}
	}
}
